import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AtAGlance } from '../components/AtAGlance';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  deleteEntrantA2Board,
  deleteEntrantImage,
  getEntrantMe,
  getNominationImages,
  submitChangeRequest,
  submitImageCreditChange,
  uploadEntrantImage,
  type EntrantSession,
  type ImageInfo,
  type Nomination,
} from '../lib/api';
import { api } from '../lib/api';
import { WAF_YELLOW_DARK } from '../theme';
import { CircularProgress } from '@mui/material';
import { A2BoardsPreview, VideoPreview } from '../components/EntryMedia';
import { a2BoardsFrom } from '../lib/entryMedia';
import { WbdFieldInput } from '../components/TermPickers';

const EDITABLE_FIELDS: Array<{ key: string; label: string; multiline?: boolean }> = [
  { key: 'project_title', label: 'Project title' },
  { key: 'nomination_code', label: 'Project number' },
  { key: 'category_name', label: 'Category' },
  { key: 'project_city', label: 'City' },
  { key: 'project_country', label: 'Country' },
  { key: 'architect_company_name', label: 'Architect company' },
  { key: 'architect_company_country', label: 'Architect company country' },
  { key: 'lead_architect_name', label: 'Lead architect' },
  { key: 'client_name', label: 'Client' },
  { key: 'project_completion_date', label: 'Completion date' },
  { key: 'google_maps_link', label: 'Google Maps link' },
  { key: 'project_summary', label: 'Project summary', multiline: true },
  // A2 boards are no longer entrant-editable free-text URLs (they move to an
  // upload-to-our-storage flow handled by staff; entrant upload is a later
  // follow-up). The preview below stays; the editable link fields are gone.
  { key: 'video_landscape_url', label: 'Landscape video link' },
  { key: 'video_portrait_url', label: 'Portrait video link' },
  { key: 'video_download_url', label: 'Video download link' },
  { key: 'video_copyright', label: 'Video copyright', multiline: true },
];

// A2-board + video fields are editable, but rendered inside their own dedicated
// sections below (next to the boards/video preview) rather than in the generic
// metadata form — so the metadata form filters them out via VISIBLE_FIELDS.
// They stay in EDITABLE_FIELDS so the draft-seed, diff and batched-submit logic
// still picks them up: editing one is just another drafted field change.
const RELOCATED_FIELD_KEYS = new Set<string>([
  'video_landscape_url', 'video_portrait_url', 'video_download_url', 'video_copyright',
]);
const VIDEO_FIELD_KEYS = [
  'video_landscape_url', 'video_portrait_url', 'video_download_url', 'video_copyright',
] as const;
const VISIBLE_FIELDS = EDITABLE_FIELDS.filter((f) => !RELOCATED_FIELD_KEYS.has(f.key));
const VIDEO_FIELDS = EDITABLE_FIELDS.filter((f) => (VIDEO_FIELD_KEYS as readonly string[]).includes(f.key));

// hero + img2..img10 — matches the backend slot allowlist
// (EvessioJsonParser.ImageFields / NominationImage.ImageType).
const IMAGE_SLOTS: Array<{ slot: string; label: string }> = [
  { slot: 'hero', label: 'Hero image' },
  ...Array.from({ length: 9 }, (_, i) => ({ slot: `img${i + 2}`, label: `Image ${i + 2}` })),
];

const MAX_IMAGE_BYTES = 40 * 1024 * 1024; // 40 MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png'];

// Return a copy of `obj` without `key`. Used to drop a slot from a draft map.
function omitKey<T>(obj: Record<string, T>, key: string): Record<string, T> {
  const next = { ...obj };
  delete next[key];
  return next;
}

const primaryBtnSx = {
  backgroundColor: WAF_YELLOW_DARK, color: 'text.primary', fontWeight: 600,
  '&:hover': { backgroundColor: '#E6B800' },
};
const outlinedBtnSx = {
  borderColor: WAF_YELLOW_DARK, color: WAF_YELLOW_DARK, fontWeight: 600,
  '&:hover': { borderColor: '#E6B800', backgroundColor: 'rgba(255,204,0,0.08)' },
};

// One line item in the draft summary — a single pending change, grouped by
// what kind of thing it touches so the entrant can scan Field / Image / Credit
// changes at a glance.
interface DraftItem {
  id: string;
  group: 'Field' | 'Image' | 'Credit';
  label: string;
  // For value changes: old → new. For a new image, `note` carries the summary.
  from?: string;
  to?: string;
  note?: string;
}

// Quote a value for display, or show a clear placeholder when it's empty.
function quoteVal(v: string | undefined): string {
  return v && v.trim() !== '' ? `"${v}"` : '(empty)';
}

/**
 * Bulleted, grouped list of every drafted change — shared by the "unsaved
 * changes" banner and the submit confirmation dialog so the two never drift.
 * Each row reads e.g. `Field — Project title: "old" → "new"`.
 */
function DraftChangeList({ items }: { items: DraftItem[] }) {
  if (items.length === 0) return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
      {items.map((it) => (
        <Box component="li" key={it.id}>
          <Typography variant="body2" component="span" sx={{ wordBreak: 'break-word' }}>
            <strong>{it.group} — {it.label}:</strong>{' '}
            {it.note ? it.note : <>{quoteVal(it.from)} → {quoteVal(it.to)}</>}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const pendingChipSx = {
  backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600, border: '1px solid #F59E0B',
} as const;
const draftChipSx = {
  backgroundColor: 'rgba(255,204,0,0.18)', color: '#8A6D00', fontWeight: 600, border: '1px dashed #E6B800',
} as const;

/**
 * A thumbnail that opens the full image in a lightbox on click (NOC-378).
 * Click the image to open; click the backdrop, the ✕, or press Esc to close.
 * The thumbnail keeps whatever sizing the caller passes via `sx`.
 */
function ClickableImage({ src, alt, sx }: { src: string; alt: string; sx?: object }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box
        component="img"
        src={src}
        alt={alt}
        onClick={() => setOpen(true)}
        sx={{ cursor: 'zoom-in', ...sx }}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', m: { xs: 1, sm: 2 } } } }}
      >
        <Box sx={{ position: 'relative', lineHeight: 0 }} onClick={() => setOpen(false)}>
          <IconButton
            aria-label="Close image"
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute', top: 8, right: 8, color: '#fff',
              bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', display: 'block', borderRadius: 1 }}
          />
        </Box>
      </Dialog>
    </>
  );
}

interface EntryImagesSectionProps {
  liveBySlot: Record<string, string>;
  creditBySlot: Record<string, string>;
  // Draft (held locally, not yet submitted) image replacements + their previews.
  imagePreviews: Record<string, string>;
  onSelectImage: (slot: string, file: File) => void;
  onClearImage: (slot: string) => void;
  // Draft credit edits (slot -> proposed value, only where it differs from live).
  creditDrafts: Record<string, string>;
  onSetCredit: (slot: string, value: string) => void;
  onClearCredit: (slot: string) => void;
  // Slots that were part of the LAST successful submit — awaiting admin approval.
  submittedImageSlots: string[];
  submittedCreditSlots: string[];
  submittedImagePreviews: Record<string, string>;
  // Slots with a REMOVAL request awaiting approval (submitted this visit).
  submittedImageDeleteSlots: string[];
  // Ask the parent to open the removal-confirm dialog for this slot.
  onRequestDelete: (slot: string) => void;
  disabled: boolean;
}

/**
 * Per-slot image + credit editing for the entrant entry view. This section is
 * fully CONTROLLED: it never talks to the server. Selecting a file or editing a
 * credit only updates the parent's local draft (via the callbacks). The whole
 * draft is sent to the server as ONE change-request batch when the entrant hits
 * "Submit changes for approval" (NOC — batch the entrant change submission).
 *
 * Three visual states per slot, clearly distinguished:
 *   - live only            → current image/credit + Replace/Edit controls
 *   - draft (unsaved)      → local preview + "Unsaved" chip + Undo
 *   - submitted (pending)  → local preview + "Pending approval" chip
 */
function EntryImagesSection({
  liveBySlot,
  creditBySlot,
  imagePreviews,
  onSelectImage,
  onClearImage,
  creditDrafts,
  onSetCredit,
  onClearCredit,
  submittedImageSlots,
  submittedCreditSlots,
  submittedImagePreviews,
  submittedImageDeleteSlots,
  onRequestDelete,
  disabled,
}: EntryImagesSectionProps) {
  const [error, setError] = useState<string | null>(null);
  // Transient inline credit-edit UI state (which slot's box is open + its value).
  const [editingCredit, setEditingCredit] = useState<string | null>(null);
  const [creditEditValue, setCreditEditValue] = useState('');
  // The slot's parent-draft value when editing began (undefined = no draft yet),
  // so Cancel/Esc can restore it after we've been writing changes through live.
  const [creditEditOriginal, setCreditEditOriginal] = useState<string | undefined>(undefined);

  const handleFile = (slot: string, file: File | undefined) => {
    setError(null);
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError(`${file.name}: only JPG and PNG images are accepted.`);
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError(`${file.name}: image is over the 40 MB limit.`);
      return;
    }
    onSelectImage(slot, file);
  };

  const startCredit = (slot: string) => {
    setError(null);
    setCreditEditOriginal(creditDrafts[slot]);
    setCreditEditValue(creditDrafts[slot] ?? creditBySlot[slot] ?? '');
    setEditingCredit(slot);
  };
  // Live-capture: every keystroke pushes the value into the parent draft so the
  // count, banner and "unsaved" chips update immediately — no separate commit
  // step where an un-applied edit could be silently dropped on submit (NOC bug:
  // the confirm dialog under-reported credits still sitting in child state).
  const changeCredit = (slot: string, value: string) => {
    setCreditEditValue(value);
    onSetCredit(slot, value);
  };
  const commitCredit = (slot: string) => {
    onSetCredit(slot, creditEditValue.trim());
    setEditingCredit(null);
  };
  const cancelCredit = (slot: string) => {
    // Undo the live edits back to whatever the draft held when editing began.
    if (creditEditOriginal === undefined) onClearCredit(slot);
    else onSetCredit(slot, creditEditOriginal);
    setEditingCredit(null);
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Images</Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Replace any slot below or edit its photographer/source credit. JPG or PNG,
        up to 40 MB. Nothing is sent until you press <strong>Submit changes for
        approval</strong> — your edits are held as a draft and reviewed by the WAF
        admin team together before they go live.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {IMAGE_SLOTS.map(({ slot, label }) => {
          const draftPreview = imagePreviews[slot];
          const submittedPreview = submittedImagePreviews[slot];
          const isImageDraft = draftPreview !== undefined;
          const isImageSubmitted = !isImageDraft && submittedImageSlots.includes(slot);
          const isImageDeletePending = submittedImageDeleteSlots.includes(slot);
          const url = draftPreview ?? submittedPreview ?? liveBySlot[slot];

          const creditDraftVal = creditDrafts[slot];
          const isCreditDraft = creditDraftVal !== undefined;
          const isCreditSubmitted = !isCreditDraft && submittedCreditSlots.includes(slot);
          const shownCredit = isCreditDraft ? creditDraftVal : (creditBySlot[slot] ?? '');

          return (
            <Stack key={slot} spacing={1} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Box
                sx={{
                  width: '100%', height: 140, borderRadius: 1, overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'action.hover',
                }}
              >
                {url ? (
                  <ClickableImage src={url} alt={label}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Typography variant="caption" color="text.disabled">No image</Typography>
                )}
              </Box>

              {isImageDeletePending ? (
                <Chip size="small" icon={<HourglassEmptyIcon />} label="Removal pending approval" sx={pendingChipSx} />
              ) : isImageDraft ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip size="small" label="New image — unsaved" sx={{ ...draftChipSx, flex: 1 }} />
                  <Button size="small" color="inherit" onClick={() => onClearImage(slot)}
                    disabled={disabled} sx={{ minWidth: 0, px: 1 }}>Undo</Button>
                </Stack>
              ) : isImageSubmitted ? (
                <Chip size="small" icon={<HourglassEmptyIcon />} label="Pending approval" sx={pendingChipSx} />
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUploadOutlinedIcon />}
                    disabled={disabled}
                    sx={{ ...outlinedBtnSx, flex: 1 }}
                  >
                    {liveBySlot[slot] ? 'Replace' : 'Upload'}
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={(e) => {
                        handleFile(slot, e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  {liveBySlot[slot] && (
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
                      onClick={() => onRequestDelete(slot)} disabled={disabled}
                      sx={{ minWidth: 0, px: 1 }}>
                      Remove
                    </Button>
                  )}
                </Stack>
              )}

              {/* Photographer / source credit — draft-gated, same as the image. */}
              <Divider flexItem sx={{ my: 0.5 }} />
              {editingCredit === slot ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <TextField
                    size="small"
                    variant="standard"
                    placeholder="Photographer / source"
                    value={creditEditValue}
                    autoFocus
                    fullWidth
                    onChange={(e) => changeCredit(slot, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitCredit(slot);
                      if (e.key === 'Escape') cancelCredit(slot);
                    }}
                  />
                  <IconButton size="small" onClick={() => commitCredit(slot)}
                    aria-label="Apply credit to draft">
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => cancelCredit(slot)}
                    aria-label="Cancel credit edit">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption"
                    color={shownCredit ? 'text.secondary' : 'text.disabled'}
                    sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {shownCredit ? `© ${shownCredit}` : 'No credit'}
                  </Typography>
                  {isCreditDraft && (
                    <Chip size="small" label="Unsaved" sx={draftChipSx} />
                  )}
                  {isCreditSubmitted && (
                    <Chip size="small" icon={<HourglassEmptyIcon />} label="Pending" sx={pendingChipSx} />
                  )}
                  {isCreditDraft ? (
                    <Button size="small" color="inherit" onClick={() => onClearCredit(slot)}
                      disabled={disabled} sx={{ minWidth: 0, px: 1 }}>Undo</Button>
                  ) : (
                    <Button size="small" startIcon={<EditIcon />} onClick={() => startCredit(slot)}
                      disabled={disabled} sx={{ ...outlinedBtnSx, border: 'none', minWidth: 0, px: 1 }}>
                      Credit
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          );
        })}
      </Box>
    </Paper>
  );
}

export function SubmitChange() {
  // Authenticated entrant session — fetched on mount from /api/entrant/me.
  // The route is already gated on the Entrant role by <ProtectedRoute>, so
  // anyone who gets here has a valid JWT.
  const [session, setSession] = useState<EntrantSession | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Entry detail. Seed the selection from router state so arriving from the
  // entries list (EntrantHome passes nomination_uuid in state) jumps straight to
  // the entry instead of making the user pick it again from the table here.
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedUuid] = useState<string | null>(
    () => (location.state as { nomination_uuid?: string } | null)?.nomination_uuid ?? null
  );
  const [nomination, setNomination] = useState<Nomination | null>(null);
  const [images, setImages] = useState<ImageInfo[]>([]);

  // ---- Local draft (nothing here is sent until "Submit changes for approval") ----
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Held image files + their object-URL previews, keyed by slot.
  const [imageFiles, setImageFiles] = useState<Record<string, File>>({});
  const [imagePreviews, setImagePreviews] = useState<Record<string, string>>({});
  // Credit edits keyed by slot (only slots whose value differs from the live one).
  const [creditDrafts, setCreditDrafts] = useState<Record<string, string>>({});

  // ---- Already-submitted-this-visit markers (awaiting admin approval) ----
  const [submittedFieldKeys, setSubmittedFieldKeys] = useState<string[]>([]);
  const [submittedImageSlots, setSubmittedImageSlots] = useState<string[]>([]);
  const [submittedCreditSlots, setSubmittedCreditSlots] = useState<string[]>([]);
  const [submittedImagePreviews, setSubmittedImagePreviews] = useState<Record<string, string>>({});
  // Removal requests submitted this visit (image slots + A2 board slots), awaiting
  // admin approval. Removals call the DELETE endpoint immediately (they're their
  // own change request), so they aren't part of the batched draft above.
  const [submittedImageDeleteSlots, setSubmittedImageDeleteSlots] = useState<string[]>([]);
  const [submittedA2DeleteSlots, setSubmittedA2DeleteSlots] = useState<string[]>([]);

  // Removal confirm dialog: which thing is being removed, plus its in-flight state.
  const [deleteTarget, setDeleteTarget] =
    useState<{ kind: 'image' | 'a2board'; slot: string; label: string } | null>(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);

  // Submit dialog + leave guard.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaveGuardOpen, setLeaveGuardOpen] = useState(false);

  // Keep a live ref of every object URL we've minted so unmount can revoke them
  // all (avoids leaking blobs when the entrant navigates away).
  const objectUrls = useRef<Set<string>>(new Set());
  useEffect(() => {
    const urls = objectUrls.current;
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = await getEntrantMe();
        setSession(s);
      } catch (err) {
        const e = err as { response?: { data?: { error?: string } }; message?: string };
        setSessionError(e.response?.data?.error || e.message || 'Unable to load your entries.');
      } finally {
        setLoadingSession(false);
      }
    })();
  }, []);

  // Load entry detail
  useEffect(() => {
    if (!selectedUuid) return;
    let cancelled = false;
    Promise.all([
      api.get<Nomination>(`/nominations/${selectedUuid}`),
      getNominationImages(selectedUuid),
    ]).then(([nomRes, imgs]) => {
      if (cancelled) return;
      setNomination(nomRes.data);
      setImages(imgs);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedUuid]);

  const liveBySlot = useMemo(() => {
    const m: Record<string, string> = {};
    images.forEach((img) => { if (img.type) m[img.type] = img.url; });
    return m;
  }, [images]);

  const creditBySlot = useMemo(() => {
    const m: Record<string, string> = {};
    images.forEach((img) => { if (img.type) m[img.type] = img.credit ?? ''; });
    return m;
  }, [images]);

  const seedDraftFromNomination = useCallback((nom: Nomination) => {
    const d: Record<string, string> = {};
    EDITABLE_FIELDS.forEach(({ key }) => { d[key] = String((nom as Record<string, unknown>)[key] ?? ''); });
    setDraft(d);
  }, []);

  const startEdit = () => {
    if (!nomination) return;
    // Preserve any already-drafted field edits; fill the rest from the entry.
    setDraft((d) => {
      const nd: Record<string, string> = {};
      EDITABLE_FIELDS.forEach(({ key }) => {
        nd[key] = d[key] ?? String((nomination as Record<string, unknown>)[key] ?? '');
      });
      return nd;
    });
    setEditing(true);
  };

  const changedFields = useMemo(() => {
    if (!nomination) return {} as Record<string, string>;
    const diff: Record<string, string> = {};
    EDITABLE_FIELDS.forEach(({ key }) => {
      const orig = String((nomination as Record<string, unknown>)[key] ?? '');
      if (draft[key] !== undefined && draft[key] !== orig) diff[key] = draft[key];
    });
    return diff;
  }, [draft, nomination]);

  // ---- Draft image handlers ----
  const handleSelectImage = useCallback((slot: string, file: File) => {
    setImagePreviews((prev) => {
      // Revoke any previous DRAFT preview for this slot before replacing it.
      if (prev[slot]) { URL.revokeObjectURL(prev[slot]); objectUrls.current.delete(prev[slot]); }
      const url = URL.createObjectURL(file);
      objectUrls.current.add(url);
      return { ...prev, [slot]: url };
    });
    setImageFiles((prev) => ({ ...prev, [slot]: file }));
    // Re-editing a slot that was previously submitted clears its pending marker.
    setSubmittedImageSlots((prev) => prev.filter((s) => s !== slot));
  }, []);

  const clearImageDraft = useCallback((slot: string) => {
    setImagePreviews((prev) => {
      if (prev[slot]) { URL.revokeObjectURL(prev[slot]); objectUrls.current.delete(prev[slot]); }
      return omitKey(prev, slot);
    });
    setImageFiles((prev) => omitKey(prev, slot));
  }, []);

  // ---- Draft credit handlers ----
  const handleSetCredit = useCallback((slot: string, value: string) => {
    const current = creditBySlot[slot] ?? '';
    setCreditDrafts((prev) => (value === current ? omitKey(prev, slot) : { ...prev, [slot]: value }));
    setSubmittedCreditSlots((prev) => prev.filter((s) => s !== slot));
  }, [creditBySlot]);

  const clearCreditDraft = useCallback((slot: string) => {
    setCreditDrafts((prev) => omitKey(prev, slot));
  }, []);

  // ---- Draft summary ----
  const fieldKeys = Object.keys(changedFields);
  const imageSlots = Object.keys(imageFiles);
  const creditSlots = Object.keys(creditDrafts);
  const draftCount = fieldKeys.length + imageSlots.length + creditSlots.length;
  const hasDraft = draftCount > 0;
  const hasSubmitted =
    submittedFieldKeys.length + submittedImageSlots.length + submittedCreditSlots.length +
    submittedImageDeleteSlots.length + submittedA2DeleteSlots.length > 0;

  // Open the removal-confirm dialog for an image slot or an A2 board.
  const requestImageDelete = useCallback((slot: string) => {
    const label = IMAGE_SLOTS.find((i) => i.slot === slot)?.label || slot;
    setDeleteTarget({ kind: 'image', slot, label });
  }, []);
  const requestA2Delete = useCallback((slot: string, label: string) => {
    setDeleteTarget({ kind: 'a2board', slot, label });
  }, []);

  // Confirm a removal → call the DELETE endpoint (raises a change request) and mark
  // the slot as "removal pending approval". The live media stays visible until an
  // admin approves — nothing disappears immediately.
  const confirmDelete = async () => {
    if (!selectedUuid || !deleteTarget) return;
    setDeleteProcessing(true);
    setError(null);
    try {
      if (deleteTarget.kind === 'image') {
        await deleteEntrantImage(selectedUuid, deleteTarget.slot);
        setSubmittedImageDeleteSlots((prev) => [...new Set([...prev, deleteTarget.slot])]);
      } else {
        await deleteEntrantA2Board(selectedUuid, deleteTarget.slot as 'a2board1' | 'a2board2');
        setSubmittedA2DeleteSlots((prev) => [...new Set([...prev, deleteTarget.slot])]);
      }
      setDeleteTarget(null);
      setSuccess(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Removal request failed');
      setDeleteTarget(null);
    } finally {
      setDeleteProcessing(false);
    }
  };

  // ---- Navigation guard: warn before leaving with an unsubmitted draft ----
  useEffect(() => {
    if (!hasDraft) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasDraft]);

  const handleBack = () => {
    if (hasDraft) { setLeaveGuardOpen(true); return; }
    navigate('/');
  };

  // Discard only the generic metadata edits, leaving any A2/video draft links
  // (owned by their own sections below) untouched. Mirrors the metadata form's
  // VISIBLE_FIELDS scope so the "Discard edits" button doesn't reach across
  // into the relocated sections.
  const discardMetadataEdits = () => {
    if (nomination) {
      setDraft((d) => {
        const nd = { ...d };
        VISIBLE_FIELDS.forEach(({ key }) => {
          nd[key] = String((nomination as Record<string, unknown>)[key] ?? '');
        });
        return nd;
      });
    }
    setEditing(false);
  };

  const discardDraft = () => {
    // Revoke draft previews and reset every draft channel.
    Object.values(imagePreviews).forEach((u) => { URL.revokeObjectURL(u); objectUrls.current.delete(u); });
    setImagePreviews({});
    setImageFiles({});
    setCreditDrafts({});
    if (nomination) seedDraftFromNomination(nomination);
    setEditing(false);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedUuid) return;
    setSubmitting(true);
    setError(null);
    // One batch id for the whole submission so the admin reviews it together.
    const batchId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      if (fieldKeys.length > 0) {
        await submitChangeRequest({
          target_entity_id: selectedUuid,
          changes: changedFields,
          reason: reason || undefined,
          batch_id: batchId,
        });
      }
      for (const slot of imageSlots) {
        await uploadEntrantImage(selectedUuid, slot, imageFiles[slot], batchId);
      }
      for (const slot of creditSlots) {
        await submitImageCreditChange(selectedUuid, slot, creditDrafts[slot], batchId);
      }

      // Move drafts into the "awaiting approval" markers. Image previews are kept
      // (ownership transferred) so the submitted slot keeps showing what was sent.
      setSubmittedFieldKeys((prev) => [...new Set([...prev, ...fieldKeys])]);
      setSubmittedImageSlots((prev) => [...new Set([...prev, ...imageSlots])]);
      setSubmittedCreditSlots((prev) => [...new Set([...prev, ...creditSlots])]);
      setSubmittedImagePreviews((prev) => ({ ...prev, ...imagePreviews }));

      // Clear the draft (previews NOT revoked — now owned by submittedImagePreviews).
      setImagePreviews({});
      setImageFiles({});
      setCreditDrafts({});
      if (nomination) seedDraftFromNomination(nomination);
      setEditing(false);
      setReason('');
      setConfirmed(false);
      setConfirmOpen(false);
      setSuccess(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Submission failed');
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- STEP 1: Loading / no-session state ----
  if (loadingSession) {
    return (
      <Stack alignItems="center" sx={{ mt: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (!session) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ mt: 8 }}>
        <LockOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="h4">Your entries</Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
          {sessionError ?? 'No entries linked to your account yet. An administrator will grant access shortly.'}
        </Typography>
      </Stack>
    );
  }

  // ---- STEP 2: No entry selected (direct nav or a page refresh that dropped
  // the router state) → send the entrant to their home page, which carries the
  // "Your details" contact card plus the entries list. ----
  if (!selectedUuid) {
    return <Navigate to="/" replace />;
  }

  // ---- STEP 3: Entry detail ----
  if (!nomination) return <Typography>Loading...</Typography>;

  // Every drafted change as one flat, grouped list — drives both the banner and
  // the confirm dialog so the count and the detail can never disagree.
  const draftItems: DraftItem[] = [
    ...fieldKeys.map((k) => ({
      id: `field-${k}`,
      group: 'Field' as const,
      label: EDITABLE_FIELDS.find((f) => f.key === k)?.label || k,
      from: String((nomination as Record<string, unknown>)[k] ?? ''),
      to: changedFields[k],
    })),
    ...imageSlots.map((s) => ({
      id: `image-${s}`,
      group: 'Image' as const,
      label: IMAGE_SLOTS.find((i) => i.slot === s)?.label || s,
      note: 'new image',
    })),
    ...creditSlots.map((s) => ({
      id: `credit-${s}`,
      group: 'Credit' as const,
      label: IMAGE_SLOTS.find((i) => i.slot === s)?.label || s,
      from: creditBySlot[s] ?? '',
      to: creditDrafts[s],
    })),
  ];

  // Live A2 boards for the preview (populated slots only).
  const a2Boards = a2BoardsFrom(nomination);
  // Populated board slots with their slot key, for the per-board removal controls.
  const a2SlotDefs = ([
    { slot: 'a2board1' as const, label: 'A2 Board 1', url: nomination.a2board1 },
    { slot: 'a2board2' as const, label: 'A2 Board 2', url: nomination.a2board2 },
  ]).filter((b) => !!b.url);
  // Submitted-this-visit counts, split per section so each shows its own
  // "awaiting approval" chip rather than lumping everything under Metadata.
  const submittedMetaCount = submittedFieldKeys.filter((k) => !RELOCATED_FIELD_KEYS.has(k)).length;
  const submittedVideoCount = submittedFieldKeys.filter((k) => (VIDEO_FIELD_KEYS as readonly string[]).includes(k)).length;

  // One editable URL row wired straight into the shared draft: value falls back
  // to the live nomination value until the entrant touches it, and any change is
  // picked up by changedFields → the banner, summary and batched submit.
  const renderDraftField = ({ key, label, multiline }: { key: string; label: string; multiline?: boolean }) => (
    <Stack key={key} spacing={0.25}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {key in changedFields && <Chip size="small" label="Edited — unsaved" sx={draftChipSx} />}
      </Stack>
      <TextField
        size="small"
        fullWidth
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        value={draft[key] ?? String((nomination as Record<string, unknown>)[key] ?? '')}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
      />
    </Stack>
  );

  return (
    <Stack spacing={3} sx={{ maxWidth: '100%', mx: 'auto' }}>
      <Box>
        <Button variant="text" color="inherit" startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ color: 'text.secondary', pl: 0 }}>
          Back to your entries
        </Button>
      </Box>

      <AtAGlance nomination={nomination} />

      {success && (
        <Alert severity="success" icon={<CheckCircleOutlineIcon />} onClose={() => setSuccess(false)}>
          Your changes have been submitted for approval as one batch. The WAF admin
          team will review them together shortly.
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {hasDraft && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ mb: draftItems.length > 0 ? 1 : 0 }}>
            <strong>You have unsaved changes — not yet submitted.</strong>{' '}
            {draftCount} change{draftCount > 1 ? 's' : ''} held as a draft. Use{' '}
            <strong>Submit changes for approval</strong> below to send them.
          </Typography>
          <DraftChangeList items={draftItems} />
        </Alert>
      )}
      {!hasDraft && hasSubmitted && (
        <Alert severity="info" icon={<HourglassEmptyIcon />}>
          Your changes are awaiting approval by the WAF admin team.
        </Alert>
      )}

      {/* Metadata section */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <Typography variant="h6">Metadata</Typography>
            {submittedMetaCount > 0 && (
              <Chip size="small" icon={<HourglassEmptyIcon />}
                label={`${submittedMetaCount} awaiting approval`} sx={pendingChipSx} />
            )}
          </Stack>
          {editing ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" startIcon={<CloseIcon />}
                onClick={discardMetadataEdits}
                sx={outlinedBtnSx}>Discard edits</Button>
              <Button variant="contained" startIcon={<CheckIcon />}
                onClick={() => setEditing(false)}
                sx={primaryBtnSx}>Done</Button>
            </Stack>
          ) : (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={startEdit}
              sx={{ ...outlinedBtnSx, alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
              Edit
            </Button>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, columnGap: 3, rowGap: 1.5 }}>
          {VISIBLE_FIELDS.map(({ key, label, multiline }) => {
            const original = String((nomination as Record<string, unknown>)[key] ?? '');
            const isChanged = key in changedFields;
            const shown = isChanged ? changedFields[key] : original;
            return (
              <Stack key={key} spacing={0.25} sx={{ gridColumn: multiline ? { sm: '1 / -1' } : undefined }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  {!editing && isChanged && (
                    <Chip size="small" label="Edited — unsaved" sx={draftChipSx} />
                  )}
                </Stack>
                {editing ? (
                  <WbdFieldInput field={key} multiline={multiline}
                    value={draft[key] ?? ''} onChange={(v) => setDraft((d) => ({ ...d, [key]: v }))} />
                ) : (
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', color: shown ? 'text.primary' : 'text.disabled' }}>
                    {shown || '—'}
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Box>
      </Paper>

      {/* Images + credits — held locally, submitted with the rest of the draft. */}
      <EntryImagesSection
        liveBySlot={liveBySlot}
        creditBySlot={creditBySlot}
        imagePreviews={imagePreviews}
        onSelectImage={handleSelectImage}
        onClearImage={clearImageDraft}
        creditDrafts={creditDrafts}
        onSetCredit={handleSetCredit}
        onClearCredit={clearCreditDraft}
        submittedImageSlots={submittedImageSlots}
        submittedCreditSlots={submittedCreditSlots}
        submittedImagePreviews={submittedImagePreviews}
        submittedImageDeleteSlots={submittedImageDeleteSlots}
        onRequestDelete={requestImageDelete}
        disabled={submitting}
      />

      {/* A2 judging boards — read-only preview plus a per-board removal request.
          Replacing a board is still handled by the WAF admin team (upload-to-
          storage); entrants can request that a board be REMOVED, which is staged as
          a change request for admin approval. */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>A2 Judging Boards</Typography>
          {submittedA2DeleteSlots.length > 0 && (
            <Chip size="small" icon={<HourglassEmptyIcon />}
              label={`${submittedA2DeleteSlots.length} removal${submittedA2DeleteSlots.length > 1 ? 's' : ''} awaiting approval`}
              sx={pendingChipSx} />
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These are the boards shown to the judges. Open a board to check it. To
          replace a board, contact the WAF admin team. To have a board removed, use
          <strong> Request removal</strong> below — an admin reviews it before it goes.
        </Typography>
        <A2BoardsPreview boards={a2Boards} buttonSx={outlinedBtnSx} />
        {a2SlotDefs.length > 0 && (
          <Stack spacing={1} sx={{ mt: 2 }}>
            {a2SlotDefs.map(({ slot, label }) => (
              <Stack key={slot} direction="row" spacing={1} alignItems="center"
                sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{label}</Typography>
                {submittedA2DeleteSlots.includes(slot) ? (
                  <Chip size="small" icon={<HourglassEmptyIcon />} label="Removal pending approval" sx={pendingChipSx} />
                ) : (
                  <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
                    onClick={() => requestA2Delete(slot, label)} disabled={submitting || deleteProcessing}>
                    Request removal
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Project video — preview (embeds / download link) plus editable links for
          all four video fields, held in the same draft. */}
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>Project Video</Typography>
          {submittedVideoCount > 0 && (
            <Chip size="small" icon={<HourglassEmptyIcon />}
              label={`${submittedVideoCount} awaiting approval`} sx={pendingChipSx} />
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <VideoPreview nomination={nomination} buttonSx={outlinedBtnSx} />
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {VIDEO_FIELDS.map((f) => renderDraftField(f))}
        </Stack>
      </Paper>

      {/* Sticky action bar — appears whenever there's an unsubmitted draft. */}
      {hasDraft && (
        <Paper
          elevation={4}
          sx={{
            position: 'sticky', bottom: 0, zIndex: 5, p: 2,
            borderTop: '2px solid', borderColor: WAF_YELLOW_DARK,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              <strong>You have unsaved changes — not yet submitted.</strong>{' '}
              {draftCount} change{draftCount > 1 ? 's' : ''} in this draft.
            </Typography>
            <Button variant="text" color="inherit" onClick={discardDraft} disabled={submitting}>
              Discard all
            </Button>
            <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={submitting}
              sx={primaryBtnSx}>
              Submit changes for approval
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Confirmation dialog — lists everything in the draft. */}
      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit changes for approval</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              You're submitting {draftCount} change{draftCount > 1 ? 's' : ''} to{' '}
              <strong>{nomination.project_title}</strong> as a single batch. An admin
              will review them together before anything is applied.
            </Typography>
            <Box sx={{ backgroundColor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <DraftChangeList items={draftItems} />
            </Box>
            <TextField size="small" fullWidth multiline minRows={2}
              label="Reason / note to admin (optional)"
              value={reason} onChange={(e) => setReason(e.target.value)} />
            <FormControlLabel
              control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />}
              label="I confirm all data is correct and will be shown on websites and touchscreens"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmSubmit}
            disabled={submitting || !confirmed} sx={primaryBtnSx}>
            {submitting ? 'Submitting...' : 'Submit for approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Removal confirm — the DELETE endpoints raise a change request, they don't
          remove anything on the spot, so make that explicit. */}
      <Dialog open={!!deleteTarget} onClose={() => !deleteProcessing && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Request removal</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Request removal of <strong>{deleteTarget?.label}</strong>
            {deleteTarget?.kind === 'a2board' ? ' (A2 board)' : ''}? This is submitted
            to the WAF admin team for approval — it isn't removed straight away, and
            it stays visible until an admin approves the removal.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteProcessing}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DeleteOutlineIcon />}
            onClick={confirmDelete} disabled={deleteProcessing}>
            {deleteProcessing ? 'Submitting…' : 'Request removal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave guard — warn before abandoning an unsubmitted draft. */}
      <Dialog open={leaveGuardOpen} onClose={() => setLeaveGuardOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You have unsaved changes — submit for approval before leaving? If you
            leave now, your draft will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => { setLeaveGuardOpen(false); navigate('/'); }}>
            Leave without submitting
          </Button>
          <Button variant="contained" onClick={() => setLeaveGuardOpen(false)} sx={primaryBtnSx}>
            Stay on page
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
